import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import {
  DeliveryPoint,
  GetDeliveryPointsFilters,
  GetDeliveryPointsParams,
  GetDeliveryPointsResponse,
} from '../types/delivery-point.type';
import { ADMIN_DELIVERY_POINTS_API_PATHS } from '../constants/delivery-points-api-paths.constants';

export type GetDeliveryPointsRequest = Omit<GetDeliveryPointsParams, 'filters'> & {
  filters: GetDeliveryPointsFilters;
};

type UseAdminGetDeliveryPointsResult = {
  isLoading: boolean;
  isRefetching: boolean;
  totalCount: number;
  deliveryPoints: DeliveryPoint[];
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetDeliveryPointsResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  totalCount: 0,
  deliveryPoints: [],
};

export function useAdminGetDeliveryPoints({
  page,
  limit,
  sortBy,
  sortDirection,
  filters,
}: GetDeliveryPointsRequest): UseAdminGetDeliveryPointsResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const serializedFilters = JSON.stringify(filters);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<GetDeliveryPointsResponse>(
        ADMIN_DELIVERY_POINTS_API_PATHS.GET_DELIVERY_POINTS,
        { params: { page, limit, sortBy, sortDirection, filters: serializedFilters } }
      );

      setResult({
        isLoading: false,
        isRefetching: false,
        totalCount: response.totalCount,
        deliveryPoints: response.deliveryPoints,
      });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(
          error,
          'Une erreur est survenue lors de la récupération des points de livraison'
        ),
        { variant: 'error' }
      );
      setResult({ isLoading: false, isRefetching: false, totalCount: 0, deliveryPoints: [] });
    }
  }, [page, limit, sortBy, sortDirection, serializedFilters, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
